import React from 'react';
import { createPortal } from 'react-dom';
import Card from '../../../../../shared/components/Card';
import { UserWithRole, PanelAccess, PanelSection, RoleDefaultAccess } from '../../../../../shared/services/userService';
import { computeEffectivePanelAccess } from '../../../panelAccess/effectivePanelAccess';

interface PanelAccessModalProps {
  user: UserWithRole;
  panelAccess: PanelAccess[];
  roleDefaultAccess: RoleDefaultAccess[];
  panelSections: PanelSection[];
  updating: boolean;
  onToggleAccess: (section: string, hasAccess: boolean) => void;
  onClose: () => void;
}

const PanelAccessModal: React.FC<PanelAccessModalProps> = ({
  user,
  panelAccess,
  roleDefaultAccess,
  panelSections,
  updating,
  onToggleAccess,
  onClose
}) => {
  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <Card className="max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-white mb-4">
          Gérer les accès au panel - {user.username}
        </h3>
        <p className="text-gray-400 mb-6 text-sm">
          Les réglages ici sont des <span className="text-slate-200">surcharges individuelles</span> : elles priment sur les accès par défaut du rôle, qui eux-mêmes priment sur les défauts du code si rien n’est défini en base.
        </p>
        
        {panelSections.length === 0 ? (
          <p className="text-gray-400">Chargement des sections...</p>
        ) : (
          <div className="space-y-3">
            {panelSections.map((section) => {
              const hasIndividualRow = panelAccess.some((a) => a.section === section.id);
              const hasAccess = computeEffectivePanelAccess(
                section.id,
                user.roles_id,
                panelAccess,
                roleDefaultAccess,
              );
              return (
                <div
                  key={section.id}
                  className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <h4 className="text-white font-semibold">{section.name}</h4>
                    <p className="text-gray-400 text-sm mt-1">{section.description}</p>
                    {hasIndividualRow ? (
                      <p className="text-sky-400/90 text-xs mt-1">Surcharge individuelle active (prioritaire).</p>
                    ) : null}
                    {panelAccess.find(a => a.section === section.id)?.reason && (
                      <p className="text-gray-500 text-xs mt-1">
                        Raison: {panelAccess.find(a => a.section === section.id)?.reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                      hasAccess
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-red-500/20 text-red-400 border border-red-500/50'
                    }`}>
                      {hasAccess ? 'Accès accordé' : 'Accès refusé'}
                    </span>
                    <button
                      onClick={() => onToggleAccess(section.id, !hasAccess)}
                      disabled={updating}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        hasAccess
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {updating ? '...' : hasAccess ? 'Retirer l\'accès' : 'Accorder l\'accès'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <button
          onClick={onClose}
          className="mt-6 w-full px-4 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
        >
          Fermer
        </button>
      </Card>
    </div>,
    document.body
  );
};

export default PanelAccessModal;

